import { normalizeWidgetTheme, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, apiGet, toggleWidgetTheme, updateSelectedSymbolInDropdown, getEChartsTooltipTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML, isEmptyWidgetData } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

export class TraderverseCryptoMarketCapChart {
  private container: HTMLElement;
  private config: WidgetConfig;
  private selectedTab: string = '1y';
  private selectedVolumeTab: string = 'total';
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private currentChart: any;
  private widgetResizeObserver: ResizeObserver | null = null;
  private lastFetchedAt: Date | null = null;

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.injectChartLibrary();
    this.attachStyles();
    this.setupEventListeners();
    await this.setupChart(this.config.symbol!);
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}" data-selected-tab="${this.selectedTab}">
          <div class="headTop">
            <h4 class="title crypto">${this.getTitleIconSvg()} Market Cap Chart</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<tooltip-wrapper id="lastUpdatedTooltip"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M0.625 6.625C0.625 7.41293 0.780195 8.19315 1.08172 8.9211C1.38325 9.64905 1.82521 10.3105 2.38236 10.8676C2.93951 11.4248 3.60095 11.8667 4.3289 12.1683C5.05685 12.4698 5.83707 12.625 6.625 12.625C7.41293 12.625 8.19315 12.4698 8.9211 12.1683C9.64905 11.8667 10.3105 11.4248 10.8676 10.8676C11.4248 10.3105 11.8667 9.64905 12.1683 8.9211C12.4698 8.19315 12.625 7.41293 12.625 6.625C12.625 5.0337 11.9929 3.50758 10.8676 2.38236C9.74242 1.25714 8.2163 0.625 6.625 0.625C5.0337 0.625 3.50758 1.25714 2.38236 2.38236C1.25714 3.50758 0.625 5.0337 0.625 6.625Z" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/> <path d="M6.625 3.2915V6.62484L8.625 8.62484" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/> </svg><tooltip><span>Last Updated</span><strong>N/A</strong></tooltip></tooltip-wrapper><i class="pi pi-ellipsis-v"></i></div>
          </div>
          <div class="inBody">

            <div class="optionsRow">
                <div class="item tabsDv padHt" id="time-tabs">
                    <button class="tab active" data-value="1y" type="button">1 Year</button>
                    <button class="tab" data-value="30d" type="button">30 Days</button>
                </div>  
                <span></span>
                <div class="item">
                    <button class="vtab active"type="button">Overview</button>
                </div>
            </div>

            <div class="chartDv" id="PriceCamprisonContainer" style="height:400px">
            </div>

          </div>
        </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;

  }



  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    dropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value;
      this.changeSymbol(value);
    });
    if (dropdown && this.config.symbol) dropdown.value = this.config.symbol;

    // Timeframe tab click handlers
    const tabsContainer = this.shadowRoot.getElementById('time-tabs');
    if (tabsContainer) {
      const buttons = Array.from(tabsContainer.querySelectorAll('button.tab')) as HTMLButtonElement[];
      buttons.forEach(btn => {
        btn.addEventListener('click', (ev) => {
          const target = ev.currentTarget as HTMLButtonElement;
          const val = target.getAttribute('data-value') || '1y';
          // update active classes
          buttons.forEach(b => b.classList.remove('active'));
          target.classList.add('active');
          // update internal state and hidden input / data attribute
          this.selectedTab = val;
          const hidden = this.shadowRoot.getElementById('selected-tab-input') as HTMLInputElement | null;
          if (hidden) hidden.value = val;
          const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement | null;
          if (widgetBox) widgetBox.setAttribute('data-selected-tab', val);
          // refresh chart for current symbol
          this.setupChart(this.config.symbol!);
        });
      });
    }

    const volTabsContainer = this.shadowRoot.getElementById('volume-tabs');
    if (volTabsContainer) {
      const vbuttons = Array.from(volTabsContainer.querySelectorAll('button.vtab')) as HTMLButtonElement[];
      vbuttons.forEach(btn => {
        btn.addEventListener('click', (ev) => {
          const target = ev.currentTarget as HTMLButtonElement;
          const val = target.getAttribute('data-value') || 'total';
          vbuttons.forEach(b => b.classList.remove('active'));
          target.classList.add('active');
          this.selectedVolumeTab = val;
          const hiddenVol = this.shadowRoot.getElementById('selected-volume-tab-input') as HTMLInputElement | null;
          if (hiddenVol) hiddenVol.value = val;
          const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement | null;
          if (widgetBox) widgetBox.setAttribute('data-volume-tab', val);
          this.setupChart(this.config.symbol!);
        });
      });
    }
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
        const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
        script.onload = () => resolve(true);
      } else {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
        script.async = true;
        script.setAttribute('data-chart-lib', 'true');
        this.shadowRoot.appendChild(script);
        script.onload = () => resolve(true);
      }
    });
  }

  private async setupChart(symbol?: string, stockInfo?: any): Promise<void> {
    try {
      const days_back = this.selectedTab === '30d' ? 30 : 365;
      const interval = this.selectedTab === '30d' ? '30d' : '1y';

      const payload = {
        symbol_1: symbol,
        symbol_2: "SPY",
        days_back,
        asset_type: stockInfo?.stockId.includes("_CRYPTO") ? "crypto" : "stocks"
      };

      const response = await apiGet({
        method: 'GET',
        url: `${APIURL}/v1/crypto/crypto_market_cap_chart?interval=${interval}`,
        body: { interval },
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error("Failed to fetch data");
      this.currentData = await response.json();
      const fromApi = (this.currentData as any)?.last_updated_at ?? (this.currentData as any)?.data?.[0]?.last_updated_at;
      this.lastFetchedAt = fromApi ? new Date(fromApi) : new Date();

      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
    } catch (err) {
      console.error("Error fetching chart data:", err);
    }
  }

  private formatLastUpdated(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York',
    };
    return date.toLocaleString('en-US', options).replace(',', '').replace(/\//g, '-') + ' EST';
  }

  private updateLastUpdatedTooltip(): void {
    const lastUpdated = this.lastFetchedAt ? this.formatLastUpdated(this.lastFetchedAt) : 'N/A';
    const tooltip = this.shadowRoot.querySelector('#lastUpdatedTooltip tooltip strong');
    if (tooltip) tooltip.innerHTML = lastUpdated.replace(' ', '<br>');
  }

  private updateDisplay(): void {
    const container = this.shadowRoot.querySelector('#PriceCamprisonContainer') as HTMLElement;

  const hasData =
    this.currentData?.data &&
    Array.isArray(this.currentData.data) &&
    this.currentData.data.length > 0;

  if (hasData) {
    container?.setAttribute('data-testing-id', 'Crypto Market Cap Chart');
  } else {
    container?.removeAttribute('data-testing-id');
  }

    this.updateLastUpdatedTooltip();
    if (!(window as any).echarts) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    if (!this.currentData || isEmptyWidgetData(this.currentData.data?.[0]?.all)) {
      if (container) container.innerHTML = getNoDataHTML();
      return;
    }
    this.renderAnalystRating();
  }

  private renderAnalystRating(): void {
    const container = this.shadowRoot.querySelector('#PriceCamprisonContainer');
    if (!container) return;
    const html = `
        <div class="marketRealtimeMain">
            <div class="chartHeader">
                <div class="marketCapRow">
                    <div class="item">
                        <h3>Market Cap</h3>
                        <h4 id="market-cap-value">--</h4>
                    </div>
                    <div class="item">
                        <h3>Volume </h3>
                        <h4 id="volume-value">--</h4>
                    </div>
                </div>
            </div>
            <div class="chart" id="marketRealtimeChart"></div>
        </div>
    `;
    container.innerHTML = html;
    this.drawChart();
  }

 private drawChart(): void {
  const echarts = (window as any).echarts;
  const container = this.shadowRoot.querySelector('#marketRealtimeChart') as HTMLElement;
  if (!container || !this.currentData?.data?.length) return;

  const allData = [...(this.currentData.data[0]?.all || [])].reverse();
  if (!allData.length) return;

  const timestamps = allData.map((item: any) =>
    new Date(item.last_updated || item.timestamp).toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
    })
  );

  const volumes = allData.map((item: any) =>
    this.selectedVolumeTab === 'altcoin'
      ? (item.defi_volume_24h || 0)
      : (item.derivatives_volume_24h || 0)
  );

  const lastItem = allData[allData.length - 1];
  const latestMarketCap = (lastItem?.btc || 0) + (lastItem?.eth || 0) + (lastItem?.other || 0);
  const latestVolume = this.selectedVolumeTab === 'altcoin'
    ? (lastItem?.defi_volume_24h || 0)
    : (lastItem?.derivatives_volume_24h || 0);

  const formattedCap = `$${(latestMarketCap / 1e9).toFixed(2)}B`;
  const formattedVol = `$${(latestVolume / 1e9).toFixed(2)}B`;

  const marketCapEl = this.shadowRoot.getElementById('market-cap-value');
  if (marketCapEl) marketCapEl.innerHTML = formattedCap;

  const volumeEl = this.shadowRoot.getElementById('volume-value');
  if (volumeEl) volumeEl.innerHTML = formattedVol;

  const chart = echarts.init(container);

  // const isBlack = this.config.theme === 'black';
  const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
  const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color');
  const axisLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-axis-color');
  const splitLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-split-line-color');
  const fontSize = getComputedStyle(widgetBox).getPropertyValue('--font-size-sm');
  
  const option = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      ...getEChartsTooltipTheme(widgetBox),
      formatter: (params: any) => {
        const item = allData[params[0].dataIndex];
        const dateObj = new Date(item.last_updated || item.timestamp);
        const day = dateObj.getDate();
        const month = dateObj.toLocaleString('en-US', { month: 'short' });
        const tsFormatted = `${day} ${month}`;

        const cap = (item?.btc || 0) + (item?.eth || 0) + (item?.other || 0);
        const fmt = (n: number) => {
          if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
          if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
          if (n >= 1e3) return `$${(n / 1e3).toFixed(2)}K`;
          return `$${n.toLocaleString()}`;
        };

        return `
          <div style="font-size:12px;">
            <b>${tsFormatted}</b><br/>
            Market Cap: ${fmt(cap)}
          </div>
        `;
      },
    },
    grid: { left: 0, right: 12, top: 10, bottom: 8, containLabel: true },
    xAxis: { type: 'category', data: timestamps, axisLine: { lineStyle: { color: axisLineColor } }, axisLabel: { color: textColor, fontSize: fontSize, align: 'left', } },
    yAxis: { type: 'value', position: 'right', axisLine: { show: false }, splitLine: { lineStyle: { color: splitLineColor } }, axisLabel: { formatter: (value: number) => `$${(value / 1e9).toFixed(1)}B`, color: textColor, fontSize: fontSize, margin: 8, } },
    series: [
      {
        name: 'Volume',
        type: 'line',
        smooth: true,
        data: volumes,
        symbol: 'none',
        lineStyle: { width: 3, color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [{ offset: 0, color: '#00c6ff' }, { offset: 1, color: '#0072ff' }]) },
        areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: 'rgba(0, 114, 255, 0.4)' }, { offset: 1, color: 'rgba(0, 114, 255, 0)' }]) },
      },
    ],
    legend: { show: false },
  };

  chart.setOption(option);
}


  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any, stockInfo: any) => this.setupChart(stockId, stockInfo), (type: any, data: any) => this.emit(type, data));
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }
  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }
  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }
  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }
  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    this.updateDisplay();
  }
  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
  }
  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M15.15 20.3C12.3125 20.3 10 17.9875 10 15.15C10 12.3125 12.3125 10 15.15 10C17.9875 10 20.3 12.3125 20.3 15.15C20.3 17.9875 18 20.3 15.15 20.3ZM15.15 11.8625C13.3375 11.8625 11.875 13.3375 11.875 15.1375C11.875 16.9375 13.35 18.4125 15.15 18.4125C16.95 18.4125 18.425 16.9375 18.425 15.1375C18.425 13.3375 16.9625 11.8625 15.15 11.8625Z" fill="currentColor"/> <path d="M15 28.4375C7.5875 28.4375 1.5625 22.4125 1.5625 15C1.5625 7.5875 7.5875 1.5625 15 1.5625C22.4125 1.5625 28.4375 7.5875 28.4375 15C28.4375 22.4125 22.4125 28.4375 15 28.4375ZM15 3.4375C8.625 3.4375 3.4375 8.625 3.4375 15C3.4375 21.375 8.625 26.5625 15 26.5625C21.375 26.5625 26.5625 21.375 26.5625 15C26.5625 8.625 21.375 3.4375 15 3.4375Z" fill="currentColor"/> <path d="M15.0625 22C14.5158 22 14.0625 21.5192 14.0625 20.9394V9.06061C14.0625 8.48081 14.5158 8 15.0625 8C15.6092 8 16.0625 8.48081 16.0625 9.06061V20.9394C16.0625 21.5192 15.6092 22 15.0625 22Z" fill="currentColor"/> </svg>`;
  }

}

declare global { interface Window { TraderverseCryptoMarketCapChart: typeof TraderverseCryptoMarketCapChart; } }
if (typeof window !== 'undefined') { window.TraderverseCryptoMarketCapChart = TraderverseCryptoMarketCapChart; }